<template>
  <div
    class="app-toast"
    :class="[`app-toast--${variant}`, { 'app-toast--inline': isInline }]"
    :role="role"
    data-locator="toast"
  >
    <app-material-icon
      class="app-toast__icon"
      :name="config.icon"
      :color="config.iconColor"
      :size="20"
    />

    <div class="app-toast__content">
      <p
        class="app-toast__message"
        :class="{ 'app-toast__message--title': Boolean(caption) }"
        data-locator="toast-message"
      >
        {{ message }}
      </p>

      <p
        v-if="caption"
        class="app-toast__caption"
        data-locator="toast-caption"
      >
        {{ caption }}
      </p>

      <div v-if="hasActionRow" class="app-toast__actions" data-locator="toast-actions">
        <button
          v-if="action"
          type="button"
          class="app-toast__action"
          data-locator="toast-action"
          @click="onActionClick"
        >
          {{ action.label }}
        </button>

        <component
          :is="link.href ? 'a' : 'button'"
          v-if="link"
          class="app-toast__link"
          data-locator="toast-link"
          :type="link.href ? undefined : 'button'"
          :href="link.href || undefined"
          :target="link.href ? '_blank' : undefined"
          :rel="link.href ? 'noopener noreferrer' : undefined"
          @click="onLinkClick"
        >
          <span class="app-toast__link-label">{{ link.label }}</span>
          <app-material-icon v-if="link.href" name="open_in_new" :color="linkIconColor" :size="16" />
        </component>
      </div>
    </div>

    <button
      type="button"
      :class="['app-toast__close', `bg-${config.closeIconBackground}`]"
      data-locator="toast-close-button"
      :aria-label="$t('common.close')"
      @click="$emit('closeToast')"
    >
      <app-material-icon name="close" color="icon-inverted" size="xs" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import AppMaterialIcon from '@/design-system/icons/AppMaterialIcon.vue'
import { TOAST_VARIANT_CONFIG, type ToastAction, type ToastLink, type ToastVariant } from '@/design-system/toast/types'

/**
 * AppToast
 * Design-system toast content. Rendered inside vuetify-sonner through the
 * `toast.*` helpers in `@/common/utils/toast`; it is not meant to be spawned
 * directly from templates.
 * @displayName Toast
 */
export default defineComponent({
  name: 'AppToast',
  components: { AppMaterialIcon },
  props: {
    /**
     * Visual variant. Drives background, leading icon and icon color.
     * @values info, success, warning, error
     */
    variant: {
      type: String as PropType<ToastVariant>,
      default: 'info',
    },
    /** Main message (title). */
    message: {
      type: String,
      required: true,
    },
    /** Optional secondary line below the message. */
    caption: {
      type: String,
      default: '',
    },
    /** Optional structured action button rendered in the slot row. */
    action: {
      type: Object as PropType<ToastAction | null>,
      default: null,
    },
    /** Optional structured link rendered in the slot row. */
    link: {
      type: Object as PropType<ToastLink | null>,
      default: null,
    },
  },
  // `closeToast` is the event vue-sonner listens to (it injects `onCloseToast` when it
  // renders a custom toast component) to remove the toast without recursing.
  emits: ['closeToast'],
  computed: {
    config () {
      return TOAST_VARIANT_CONFIG[this.variant]
    },
    hasActionRow (): boolean {
      return Boolean(this.action || this.link)
    },
    linkIconColor (): string {
      return this.variant === 'error' ? 'icon-inverted' : 'icon-link'
    },
    isInline (): boolean {
      // No caption: the action row sits on the same line as the icon, message and
      // close button instead of wrapping below the message.
      return this.hasActionRow && !this.caption
    },
    role (): string {
      // Warnings/errors are assertive, info/success are polite.
      return this.variant === 'error' || this.variant === 'warning' ? 'alert' : 'status'
    },
  },
  methods: {
    onActionClick () {
      try {
        this.action?.onClick()
      } finally {
        this.$emit('closeToast')
      }
    },
    onLinkClick () {
      this.link?.onClick?.()
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/box-shadow-tokens";

.app-toast {
  box-sizing: border-box;
  width: 100%;
  max-width: 320px;
  display: flex;
  align-items: flex-start;
  column-gap: 8px;
  padding: 16px;
  @include border-radius('border-radius-3');
  background-color: rgb(var(--v-theme-surface-inverted));
  color: rgb(var(--v-theme-text-inverted));
  @include box-shadow('box-shadow-medium');

  &--error {
    background-color: rgb(var(--v-theme-surface-danger-bold));

    .app-toast__action {
      &:hover {
        background-color: rgb(var(--v-theme-surface-danger-bold-hover));
      }

      &:active {
        background-color: rgb(var(--v-theme-surface-danger-bold-pressed));
      }
    }

    .app-toast__link {
      color: rgb(var(--v-theme-text-inverted));
    }
  }

  // Single-line layout: with no caption, the icon, message, action row and close
  // button are all aligned on one horizontal line.
  &--inline {
    align-items: center;

    .app-toast__content {
      flex-direction: row;
      align-items: center;
      column-gap: 20px;
    }

    .app-toast__message {
      flex: 1 1 auto;
      margin: 0;
    }

    .app-toast__actions {
      flex-shrink: 0;
      margin-top: 0;
      column-gap: 8px;
    }
  }

  &__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 20px;
  }

  &__content {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    row-gap: 2px;
  }

  &__message {
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    margin: 0 20px 0 0;

    &--title {
      font-weight: 700;
    }
  }

  &__caption {
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    margin: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    column-gap: 16px;
    margin-top: 8px;
  }

  &__action {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    color: rgb(var(--v-theme-text-inverted));
    background: none;
    border: none;
    height: 28px;
    padding: 0 12px;
    @include border-radius('border-radius-2');
    cursor: pointer;

    &:hover {
      background-color: rgb(var(--v-theme-surface-brand-bold-hover));
    }

    &:active {
      background-color: rgb(var(--v-theme-surface-brand-bold-pressed));
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    column-gap: 4px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    color: rgb(var(--v-theme-text-link));
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: none;

    &:hover .app-toast__link-label {
      text-decoration: underline;
    }
  }

  &__link-label {
    text-decoration: none;
  }

  &__close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    @include border-radius('border-radius-circle');
    border: none;
    padding: 0;
    cursor: pointer;
    color: rgb(var(--v-theme-icon-inverted));
  }
}
</style>
